import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import ResponsiveTableWrapper from '../components/ResponsiveTableWrapper';
import { useAuth } from '../contexts/AuthContext';
import {
  portalCandidateService,
  PortalCandidate,
  CandidateFilters,
} from '../api/portalCandidateService';

const REFERENCE_TYPES = [
  { value: 'pastor', label: 'Pastor/Minister' },
  { value: 'employer', label: 'Employer/Supervisor' },
  { value: 'friend', label: 'Friend' },
  { value: 'mentor', label: 'Mentor' },
  { value: 'colleague', label: 'Colleague' },
  { value: 'other', label: 'Other' },
];

const THEOLOGY_FIELDS = [
  { key: 'scriptureAffirmation', label: 'Scripture Affirmation' },
  { key: 'trinityUnderstanding', label: 'Trinity Understanding' },
  { key: 'originalSinUnderstanding', label: 'Original Sin Understanding' },
  { key: 'christologyUnderstanding', label: 'Christology Understanding' },
  { key: 'salvationDoctrines', label: 'Salvation Doctrines' },
  { key: 'gospelPresentation', label: 'Gospel Presentation' },
  { key: 'churchNatureMission', label: 'Church Nature & Mission' },
  { key: 'churchOrdinances', label: 'Church Ordinances' },
  { key: 'churchMembership', label: 'Church Membership' },
  { key: 'churchOffices', label: 'Church Offices' },
  { key: 'eschatologicalConvictions', label: 'Eschatological Convictions' },
  { key: 'eschatologyMinistryImpact', label: 'Eschatology Ministry Impact' },
  { key: 'believersBaptism', label: "Believer's Baptism" },
  { key: 'churchAutonomy', label: 'Church Autonomy' },
  { key: 'preachingPhilosophy', label: 'Preaching Philosophy' },
  { key: 'discipleMaking', label: 'Disciple Making' },
  { key: 'worshipPhilosophy', label: 'Worship Philosophy' },
  { key: 'evangelismSocialEngagement', label: 'Evangelism & Social Engagement' },
  { key: 'theologicalDisagreement', label: 'Theological Disagreement' },
  { key: 'gospelPluralisticCulture', label: 'Gospel in Pluralistic Culture' },
  { key: 'contextualizationView', label: 'Contextualization View' },
  { key: 'plantingChallenges', label: 'Planting Challenges' },
  { key: 'personalHoliness', label: 'Personal Holiness' },
  { key: 'marriageFamilyProtection', label: 'Marriage & Family Protection' },
  { key: 'conflictHandling', label: 'Conflict Handling' },
];

const SPIRITUAL_HEALTH_FIELDS = [
  { key: 'currentWalkWithLord', label: 'Current Walk with the Lord' },
  { key: 'intimacyWithChristHindrances', label: 'Intimacy with Christ Hindrances' },
  { key: 'spiritualDrynessResponse', label: 'Spiritual Dryness Response' },
  { key: 'soulGuarding', label: 'Soul Guarding' },
  { key: 'restAndSabbathRhythms', label: 'Rest and Sabbath Rhythms' },
  { key: 'recentConvictionResponse', label: 'Recent Conviction Response' },
  { key: 'emotionalStabilityDescription', label: 'Emotional Stability Description' },
  { key: 'pressureCriticismResponse', label: 'Pressure & Criticism Response' },
  { key: 'stressDisappointmentHandling', label: 'Stress & Disappointment Handling' },
  { key: 'healthyBoundaries', label: 'Healthy Boundaries' },
  { key: 'currentEmotionalState', label: 'Current Emotional State' },
  { key: 'burnoutPrevention', label: 'Burnout Prevention' },
  { key: 'godsCallDescription', label: "God's Call Description" },
  { key: 'callConfirmationByOthers', label: 'Call Confirmation by Others' },
  { key: 'difficultMoments', label: 'Difficult Moments' },
  { key: 'discerningGodsLeading', label: "Discerning God's Leading" },
  { key: 'faithAndWisdomBalance', label: 'Faith and Wisdom Balance' },
  { key: 'churchPlantingFears', label: 'Church Planting Fears' },
  { key: 'feedbackCorrectionResponse', label: 'Feedback & Correction Response' },
  { key: 'conflictResolutionApproach', label: 'Conflict Resolution Approach' },
  { key: 'shepherdingDifficultPeople', label: 'Shepherding Difficult People' },
  { key: 'fosteringUnity', label: 'Fostering Unity' },
  { key: 'relationalIntegrity', label: 'Relational Integrity' },
  { key: 'accountabilityRelationships', label: 'Accountability Relationships' },
  { key: 'spiritualAttackRecognition', label: 'Spiritual Attack Recognition' },
  { key: 'prayerInDecisions', label: 'Prayer in Decisions' },
  { key: 'guardingAgainstCynicism', label: 'Guarding Against Cynicism' },
  { key: 'godlyProvisionExample', label: 'Godly Provision Example' },
  { key: 'prayingTogether', label: 'Praying Together' },
  { key: 'sustainingScriptures', label: 'Sustaining Scriptures' },
  { key: 'pastorMentorAccountability', label: 'Pastor/Mentor Accountability' },
  { key: 'discipleshipFrequency', label: 'Discipleship Frequency' },
  { key: 'teachability', label: 'Teachability' },
  { key: 'isolationPrevention', label: 'Isolation Prevention' },
  { key: 'spouseSupportNeeds', label: 'Spouse Support Needs' },
  { key: 'handlingUnappreciation', label: 'Handling Unappreciation' },
];

const PortalCandidates: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = !!user?.roles?.includes('admin');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [onboardingFilter, setOnboardingFilter] = useState<string>('');
  const [selectedCandidate, setSelectedCandidate] = useState<PortalCandidate | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [pdfDownloadingId, setPdfDownloadingId] = useState<number | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [profileForm, setProfileForm] = useState<any>(null);
  const [theologyForm, setTheologyForm] = useState<any>(null);
  const [sendingChurchForm, setSendingChurchForm] = useState<any>(null);
  const [spiritualCandidateForm, setSpiritualCandidateForm] = useState<any>(null);
  const [spiritualSpouseForm, setSpiritualSpouseForm] = useState<any>(null);
  const [spiritualFlags, setSpiritualFlags] = useState({
    candidateSubmitted: false,
    spouseSubmitted: false,
    spiritualHealthCompleted: false,
    spiritualHealthNotes: '',
  });
  const [referencesForm, setReferencesForm] = useState<any[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [activeEditSection, setActiveEditSection] = useState<string | null>(null);

  const filters: CandidateFilters = {
    page,
    limit: 20,
    search: search || undefined,
    status: statusFilter || undefined,
    onboardingCompleted:
      onboardingFilter === 'completed'
        ? true
        : onboardingFilter === 'in_progress'
        ? false
        : undefined,
  };

  // Fetch candidates
  const { data: candidatesData, isLoading } = useQuery({
    queryKey: ['portalCandidates', filters],
    queryFn: () => portalCandidateService.getAllCandidates(filters),
  });

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['portalCandidateStats'],
    queryFn: () => portalCandidateService.getOnboardingStats(),
  });

  const { data: candidateDetailData, isLoading: isDetailLoading } = useQuery({
    queryKey: ['portalCandidateDetail', selectedCandidate?.id],
    queryFn: () => portalCandidateService.getCandidate(selectedCandidate!.id),
    enabled: !!selectedCandidate && showDetailModal,
  });

  const candidates = candidatesData?.data || [];
  const pagination = candidatesData?.pagination;
  const stats = statsData?.data;
  const detailCandidate = (candidateDetailData?.data || selectedCandidate) as any;

  const getStatusBadge = (status: string) => {
    const statusStyles: Record<string, string> = {
      application_started: 'bg-gray-100 text-gray-800',
      application_submitted: 'bg-blue-100 text-blue-800',
      application_reviewed: 'bg-indigo-100 text-indigo-800',
      assessment_scheduled: 'bg-yellow-100 text-yellow-800',
      assessment_completed: 'bg-purple-100 text-purple-800',
      assessment_passed: 'bg-green-100 text-green-800',
      assessment_failed: 'bg-red-100 text-red-800',
      withdrawn: 'bg-red-100 text-red-800',
    };
    return statusStyles[status] || 'bg-gray-100 text-gray-800';
  };

  const handleDownloadPdf = async (candidate: PortalCandidate) => {
    if (!candidate?.id) return;
    setPdfDownloadingId(candidate.id);
    setPdfError(null);
    try {
      await portalCandidateService.downloadCandidatePdf(candidate.id, candidate.name);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        (error?.message ? `Failed to download PDF: ${error.message}` : 'Failed to download PDF');
      setPdfError(message);
    } finally {
      setPdfDownloadingId(null);
    }
  };

  const handleSendEmail = async () => {
    if (!selectedCandidate || !recipientEmail) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipientEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setSendingEmail(true);
    setEmailError(null);
    setEmailSuccess(null);

    try {
      await portalCandidateService.sendCandidateDetails(selectedCandidate.id, recipientEmail);
      setEmailSuccess(`Successfully sent candidate details to ${recipientEmail}`);
      setRecipientEmail('');
    } catch (error: any) {
      setEmailError(error.response?.data?.message || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const formatDate = (date?: string) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  const formatBoolean = (val?: boolean) => (val ? 'Yes' : 'No');

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setShowDeleteModal(false);
    setShowEditModal(false);
    setActiveEditSection(null);
    setSelectedCandidate(null);
    setRecipientEmail('');
    setEmailSuccess(null);
    setEmailError(null);
    setPdfError(null);
    setDeleteError(null);
    setProfileForm(null);
    setTheologyForm(null);
    setSendingChurchForm(null);
    setSpiritualCandidateForm(null);
    setSpiritualSpouseForm(null);
    setReferencesForm([]);
    setSpiritualFlags({
      candidateSubmitted: false,
      spouseSubmitted: false,
      spiritualHealthCompleted: false,
      spiritualHealthNotes: '',
    });
    setSaveError(null);
    setSaveSuccess(null);
  };

  const openEditModal = (section: string) => {
    setActiveEditSection(section);
    setShowEditModal(true);
    setSaveError(null);
    setSaveSuccess(null);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setActiveEditSection(null);
  };

  const deleteCandidateMutation = useMutation({
    mutationFn: (candidateId: number) => portalCandidateService.deleteCandidate(candidateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portalCandidates'] });
      queryClient.invalidateQueries({ queryKey: ['portalCandidateStats'] });
      closeDetailModal();
    },
    onError: (error: any) => {
      setDeleteError(error?.response?.data?.message || 'Failed to delete candidate');
    },
  });

  const updateProfileMutation = useMutation({
    mutationFn: (payload: any) => portalCandidateService.updateCandidate(payload.id, payload.data),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['portalCandidates'] });
      queryClient.invalidateQueries({ queryKey: ['portalCandidateDetail'] });
      setSelectedCandidate(response.data);
      setSaveSuccess('Profile updated');
      setSaveError(null);
    },
    onError: (error: any) => {
      setSaveError(error?.response?.data?.message || 'Failed to update profile');
    },
  });

  const updateTheologyMutation = useMutation({
    mutationFn: (payload: any) => portalCandidateService.updateTheologicalSurvey(payload.id, payload.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portalCandidates'] });
      queryClient.invalidateQueries({ queryKey: ['portalCandidateDetail'] });
      setSaveSuccess('Theological survey updated');
      setSaveError(null);
    },
    onError: (error: any) => {
      setSaveError(error?.response?.data?.message || 'Failed to update theological survey');
    },
  });

  const updateSpiritualMutation = useMutation({
    mutationFn: (payload: any) => portalCandidateService.updateSpiritualHealthSurveys(payload.id, payload.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portalCandidates'] });
      queryClient.invalidateQueries({ queryKey: ['portalCandidateDetail'] });
      setSaveSuccess('Spiritual health surveys updated');
      setSaveError(null);
    },
    onError: (error: any) => {
      setSaveError(error?.response?.data?.message || 'Failed to update spiritual health surveys');
    },
  });

  const updateReferencesMutation = useMutation({
    mutationFn: (payload: any) => portalCandidateService.updateReferences(payload.id, payload.references),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portalCandidates'] });
      queryClient.invalidateQueries({ queryKey: ['portalCandidateDetail'] });
      setSaveSuccess('References updated');
      setSaveError(null);
    },
    onError: (error: any) => {
      setSaveError(error?.response?.data?.message || 'Failed to update references');
    },
  });

  const updateSendingChurchMutation = useMutation({
    mutationFn: (payload: any) => portalCandidateService.updateSendingChurch(payload.id, payload.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portalCandidates'] });
      queryClient.invalidateQueries({ queryKey: ['portalCandidateDetail'] });
      setSaveSuccess('Sending church updated');
      setSaveError(null);
    },
    onError: (error: any) => {
      setSaveError(error?.response?.data?.message || 'Failed to update sending church');
    },
  });

  const getOnboardingProgress = (candidate: PortalCandidate) => {
    // Use unified progress calculation matching backend logic
    let completedSteps = 0;
    const totalSteps = 10; // 10 main steps (excluding completion step)
    
    const pc = candidate as any;

    // Step 1: Profile
    if (pc.profileCompleted) completedSteps++;

    // Step 2: Why Church Planting
    if (pc.whyChurchPlantingCompleted) completedSteps++;
    
    // Step 3: References - simplified for admin view (no reference details available)
    if (pc.referencesCompleted) {
      completedSteps += 0.5; // Give partial credit if submitted (hub contact status not available)
    }
    
    // Step 4: Sending Church
    if (pc.sendingChurchCompleted) completedSteps++;
    
    // Step 5: Background Check - only count as complete if admin has marked it as 'received'
    if (candidate.backgroundCheckStatus === 'received') {
      completedSteps++;
    } else if (candidate.backgroundCheckStatus === 'submitted') {
      completedSteps += 0.5; // Partial credit if submitted but not received
    }
    
    // Step 6: Prepare/Enrich - if not married, count as complete
    const maritalStatus = pc.maritalStatus;
    if (maritalStatus !== 'married') {
      completedSteps++;
    } else if (candidate.prepareEnrichStatus === 'complete') {
      completedSteps++;
    } else if (candidate.prepareEnrichStatus === 'scheduled' || candidate.prepareEnrichStatus === 'started') {
      completedSteps += 0.5;
    }

    // Step 7: Spouse Interview - if not married, count as complete
    if (maritalStatus !== 'married') {
      completedSteps++;
    } else if (pc.spouseInterviewStatus === 'complete') {
      completedSteps++;
    } else if (pc.spouseInterviewSubmitted) {
      completedSteps += 0.5;
    }

    // Step 8: Theology
    if (pc.theologyCompleted) completedSteps++;
    
    // Step 9: Spiritual Health - count based on marital status
    if (pc.spiritualHealthCompleted) {
      completedSteps++;
    } else if (maritalStatus !== 'married' && pc.spiritualHealthCandidateSubmitted) {
      completedSteps += 0.5;
    } else if (maritalStatus === 'married') {
      const bothSubmitted = pc.spiritualHealthCandidateSubmitted && pc.spiritualHealthSpouseSubmitted;
      const oneSubmitted = pc.spiritualHealthCandidateSubmitted || pc.spiritualHealthSpouseSubmitted;
      if (bothSubmitted) {
        completedSteps += 0.5;
      } else if (oneSubmitted) {
        completedSteps += 0.25;
      }
    }

    // Step 10: Ministry Transitions
    if (pc.ministryTransitionsCompleted) completedSteps++;
    
    return Math.round((completedSteps / totalSteps) * 100);
  };

  const getPortalProgressPercent = (candidate: PortalCandidate) =>
    typeof (candidate as any).portalProgressPercentage === 'number'
      ? (candidate as any).portalProgressPercentage
      : getOnboardingProgress(candidate);

  const getTeamProgressPercent = (candidate: PortalCandidate) =>
    typeof (candidate as any).teamProgressPercentage === 'number' ? (candidate as any).teamProgressPercentage : 0;

  const defaultSendingChurch = {
    noSendingChurch: false,
    noSendingChurchExplanation: '',
    churchName: '',
    denomination: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States',
    },
    website: '',
    phone: '',
    email: '',
    primaryContactName: '',
    primaryContactTitle: '',
    primaryContactPhone: '',
    primaryContactEmail: '',
    relationshipDescription: '',
    yearsAttended: '',
    membershipStatus: '',
    involvementLevel: '',
    financialSupport: false,
    financialSupportAmount: '',
    financialSupportFrequency: '',
    prayerSupport: false,
    endorsementProvided: false,
    endorsementDate: '',
    endorsementDocument: '',
    churchSize: '',
    churchAge: '',
    churchPlantingExperience: false,
    churchPlantingHistory: '',
    additionalNotes: '',
  };

  useEffect(() => {
    if (!detailCandidate) return;
    setProfileForm({
      firstName: detailCandidate.firstName || '',
      lastName: detailCandidate.lastName || '',
      email: detailCandidate.email || '',
      phone: detailCandidate.phone || '',
      dateOfBirth: detailCandidate.dateOfBirth || '',
      maritalStatus: detailCandidate.maritalStatus || '',
      wifeFirstName: detailCandidate.wifeFirstName || '',
      wifeLastName: detailCandidate.wifeLastName || '',
      wifeEmail: detailCandidate.wifeEmail || '',
      wifePhone: detailCandidate.wifePhone || '',
      childrenNamesAges: detailCandidate.childrenNamesAges || '',
      targetLocationMississippi: detailCandidate.targetLocationMississippi || '',
      willBeSouthernBaptist: detailCandidate.willBeSouthernBaptist || '',
      churchName: detailCandidate.churchName || '',
      supportingChurch: detailCandidate.supportingChurch || '',
      ministryLeadershipPositions: detailCandidate.ministryLeadershipPositions || '',
      ministryInfluences: detailCandidate.ministryInfluences || '',
      theologicalTraining: detailCandidate.theologicalTraining || '',
      sermonReferenceUrl: detailCandidate.sermonReferenceUrl || '',
      willEmailSermon: !!detailCandidate.willEmailSermon,
      profileCompleted: !!detailCandidate.profileCompleted,
    });

    setTheologyForm({
      ...THEOLOGY_FIELDS.reduce((acc, field) => ({ ...acc, [field.key]: '' }), {}),
      ...(detailCandidate.theologicalSurvey || {}),
    });

    setSendingChurchForm({
      ...defaultSendingChurch,
      ...(detailCandidate.sendingChurch || {}),
    });

    const surveys = detailCandidate.spiritualHealthSurveys || [];
    const candidateSurvey = surveys.find((survey: any) => survey.surveyFor === 'candidate') || {};
    const spouseSurvey = surveys.find((survey: any) => survey.surveyFor === 'spouse') || {};
    setSpiritualCandidateForm({
      ...SPIRITUAL_HEALTH_FIELDS.reduce((acc, field) => ({ ...acc, [field.key]: '' }), {}),
      ...candidateSurvey,
    });
    setSpiritualSpouseForm({
      ...SPIRITUAL_HEALTH_FIELDS.reduce((acc, field) => ({ ...acc, [field.key]: '' }), {}),
      ...spouseSurvey,
    });
    setSpiritualFlags({
      candidateSubmitted: !!detailCandidate.spiritualHealthCandidateSubmitted,
      spouseSubmitted: !!detailCandidate.spiritualHealthSpouseSubmitted,
      spiritualHealthCompleted: !!detailCandidate.spiritualHealthCompleted,
      spiritualHealthNotes: detailCandidate.spiritualHealthNotes || '',
    });
    setReferencesForm(detailCandidate.references || []);
  }, [detailCandidate]);

  useEffect(() => {
    if (candidateDetailData?.data) {
      setSelectedCandidate(candidateDetailData.data);
    }
  }, [candidateDetailData?.data]);


  const handleProfileSave = () => {
    if (!detailCandidate || !profileForm) return;
    updateProfileMutation.mutate({ id: detailCandidate.id, data: profileForm });
  };

  const handleTheologySave = () => {
    if (!detailCandidate || !theologyForm) return;
    updateTheologyMutation.mutate({ id: detailCandidate.id, data: theologyForm });
  };

  const handleSpiritualSave = () => {
    if (!detailCandidate) return;
    updateSpiritualMutation.mutate({
      id: detailCandidate.id,
      data: {
        candidateSurvey: spiritualCandidateForm,
        spouseSurvey: spiritualSpouseForm,
        candidateSubmitted: spiritualFlags.candidateSubmitted,
        spouseSubmitted: spiritualFlags.spouseSubmitted,
        spiritualHealthCompleted: spiritualFlags.spiritualHealthCompleted,
        spiritualHealthNotes: spiritualFlags.spiritualHealthNotes,
      },
    });
  };

  const handleReferencesSave = () => {
    if (!detailCandidate) return;
    updateReferencesMutation.mutate({ id: detailCandidate.id, references: referencesForm });
  };

  const handleSendingChurchSave = () => {
    if (!detailCandidate || !sendingChurchForm) return;
    updateSendingChurchMutation.mutate({ id: detailCandidate.id, data: sendingChurchForm });
  };

  const updateReferenceField = (index: number, field: string, value: any) => {
    setReferencesForm((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const updateProfileField = (field: string, value: any) => {
    setProfileForm((prev: any) => ({ ...(prev || {}), [field]: value }));
  };

  const updateTheologyField = (field: string, value: any) => {
    setTheologyForm((prev: any) => ({ ...(prev || {}), [field]: value }));
  };

  const updateSendingChurchField = (field: string, value: any) => {
    setSendingChurchForm((prev: any) => ({ ...(prev || {}), [field]: value }));
  };

  const updateSendingChurchAddressField = (field: string, value: any) => {
    setSendingChurchForm((prev: any) => ({
      ...(prev || {}),
      address: {
        ...(prev?.address || {}),
        [field]: value,
      },
    }));
  };

  const updateSpiritualCandidateField = (field: string, value: any) => {
    setSpiritualCandidateForm((prev: any) => ({ ...(prev || {}), [field]: value }));
  };

  const updateSpiritualSpouseField = (field: string, value: any) => {
    setSpiritualSpouseForm((prev: any) => ({ ...(prev || {}), [field]: value }));
  };

  const addReference = () => {
    setReferencesForm((prev) => [
      ...prev,
      {
        name: '',
        email: '',
        phone: '',
        relationshipType: 'pastor',
        relationshipDescription: '',
        yearsKnown: '',
        organization: '',
        position: '',
      },
    ]);
  };

  const removeReference = (index: number) => {
    setReferencesForm((prev) => prev.filter((_, idx) => idx !== index));
  };

  const modalCandidate = (detailCandidate || selectedCandidate) as any;

  const renderEditModalContent = () => {
    if (!activeEditSection) return null;
    switch (activeEditSection) {
      case 'profile':
        if (!profileForm) return null;
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600">First Name</label>
                <input
                  type="text"
                  value={profileForm.firstName}
                  onChange={(e) => updateProfileField('firstName', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600">Last Name</label>
                <input
                  type="text"
                  value={profileForm.lastName}
                  onChange={(e) => updateProfileField('lastName', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600">Email</label>
                <input
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => updateProfileField('email', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600">Phone</label>
                <input
                  type="text"
                  value={profileForm.phone}
                  onChange={(e) => updateProfileField('phone', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600">Date of Birth</label>
                <input
                  type="date"
                  value={profileForm.dateOfBirth}
                  onChange={(e) => updateProfileField('dateOfBirth', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600">Marital Status</label>
                <select
                  value={profileForm.maritalStatus}
                  onChange={(e) => updateProfileField('maritalStatus', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                >
                  <option value="">Select...</option>
                  <option value="single">Single</option>
                  <option value="married">Married</option>
                  <option value="divorced">Divorced</option>
                  <option value="widowed">Widowed</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600">Wife First Name</label>
                <input
                  type="text"
                  value={profileForm.wifeFirstName}
                  onChange={(e) => updateProfileField('wifeFirstName', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600">Wife Last Name</label>
                <input
                  type="text"
                  value={profileForm.wifeLastName}
                  onChange={(e) => updateProfileField('wifeLastName', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600">Wife Email</label>
                <input
                  type="email"
                  value={profileForm.wifeEmail}
                  onChange={(e) => updateProfileField('wifeEmail', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600">Wife Phone</label>
                <input
                  type="text"
                  value={profileForm.wifePhone}
                  onChange={(e) => updateProfileField('wifePhone', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600">Children Names & Ages</label>
              <textarea
                rows={2}
                value={profileForm.childrenNamesAges}
                onChange={(e) => updateProfileField('childrenNamesAges', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600">Target Location</label>
                <input
                  type="text"
                  value={profileForm.targetLocationMississippi}
                  onChange={(e) => updateProfileField('targetLocationMississippi', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600">Will Be Southern Baptist</label>
                <select
                  value={profileForm.willBeSouthernBaptist}
                  onChange={(e) => updateProfileField('willBeSouthernBaptist', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                >
                  <option value="">Select...</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="unsure">Unsure</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600">Church Name</label>
                <input
                  type="text"
                  value={profileForm.churchName}
                  onChange={(e) => updateProfileField('churchName', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600">Supporting Church</label>
                <input
                  type="text"
                  value={profileForm.supportingChurch}
                  onChange={(e) => updateProfileField('supportingChurch', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600">Ministry Leadership Positions</label>
              <textarea
                rows={2}
                value={profileForm.ministryLeadershipPositions}
                onChange={(e) => updateProfileField('ministryLeadershipPositions', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600">Ministry Influences</label>
              <textarea
                rows={2}
                value={profileForm.ministryInfluences}
                onChange={(e) => updateProfileField('ministryInfluences', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600">Theological Training</label>
              <textarea
                rows={2}
                value={profileForm.theologicalTraining}
                onChange={(e) => updateProfileField('theologicalTraining', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600">Sermon Reference URL</label>
                <input
                  type="text"
                  value={profileForm.sermonReferenceUrl}
                  onChange={(e) => updateProfileField('sermonReferenceUrl', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                />
              </div>
              <div className="flex items-center gap-2 mt-6">
                <input
                  type="checkbox"
                  checked={profileForm.willEmailSermon}
                  onChange={(e) => updateProfileField('willEmailSermon', e.target.checked)}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">Will email sermon</span>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  checked={profileForm.profileCompleted}
                  onChange={(e) => updateProfileField('profileCompleted', e.target.checked)}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">Profile completed</span>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleProfileSave}
                disabled={updateProfileMutation.isPending}
                className="inline-flex items-center px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {updateProfileMutation.isPending ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </div>
        );
      case 'sendingChurch':
        if (!sendingChurchForm) return null;
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!sendingChurchForm.noSendingChurch}
                onChange={(e) => updateSendingChurchField('noSendingChurch', e.target.checked)}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">No sending church</span>
            </div>
            {sendingChurchForm.noSendingChurch ? (
              <div>
                <label className="block text-xs font-medium text-gray-600">Explanation</label>
                <textarea
                  rows={3}
                  value={sendingChurchForm.noSendingChurchExplanation || ''}
                  onChange={(e) => updateSendingChurchField('noSendingChurchExplanation', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600">Church Name</label>
                    <input
                      type="text"
                      value={sendingChurchForm.churchName || ''}
                      onChange={(e) => updateSendingChurchField('churchName', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600">Denomination</label>
                    <input
                      type="text"
                      value={sendingChurchForm.denomination || ''}
                      onChange={(e) => updateSendingChurchField('denomination', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600">Website</label>
                    <input
                      type="text"
                      value={sendingChurchForm.website || ''}
                      onChange={(e) => updateSendingChurchField('website', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600">Church Phone</label>
                    <input
                      type="text"
                      value={sendingChurchForm.phone || ''}
                      onChange={(e) => updateSendingChurchField('phone', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600">Church Email</label>
                    <input
                      type="email"
                      value={sendingChurchForm.email || ''}
                      onChange={(e) => updateSendingChurchField('email', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-600">Street</label>
                    <input
                      type="text"
                      value={sendingChurchForm.address?.street || ''}
                      onChange={(e) => updateSendingChurchAddressField('street', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600">City</label>
                    <input
                      type="text"
                      value={sendingChurchForm.address?.city || ''}
                      onChange={(e) => updateSendingChurchAddressField('city', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600">State</label>
                    <input
                      type="text"
                      value={sendingChurchForm.address?.state || ''}
                      onChange={(e) => updateSendingChurchAddressField('state', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600">ZIP</label>
                    <input
                      type="text"
                      value={sendingChurchForm.address?.zipCode || ''}
                      onChange={(e) => updateSendingChurchAddressField('zipCode', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600">Primary Contact Name</label>
                    <input
                      type="text"
                      value={sendingChurchForm.primaryContactName || ''}
                      onChange={(e) => updateSendingChurchField('primaryContactName', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600">Primary Contact Title</label>
                    <input
                      type="text"
                      value={sendingChurchForm.primaryContactTitle || ''}
                      onChange={(e) => updateSendingChurchField('primaryContactTitle', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600">Primary Contact Phone</label>
                    <input
                      type="text"
                      value={sendingChurchForm.primaryContactPhone || ''}
                      onChange={(e) => updateSendingChurchField('primaryContactPhone', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600">Primary Contact Email</label>
                    <input
                      type="email"
                      value={sendingChurchForm.primaryContactEmail || ''}
                      onChange={(e) => updateSendingChurchField('primaryContactEmail', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600">Relationship Description</label>
                  <textarea
                    rows={3}
                    value={sendingChurchForm.relationshipDescription || ''}
                    onChange={(e) => updateSendingChurchField('relationshipDescription', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  />
                </div>
              </>
            )}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSendingChurchSave}
                disabled={updateSendingChurchMutation.isPending}
                className="inline-flex items-center px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {updateSendingChurchMutation.isPending ? 'Saving...' : 'Save Sending Church'}
              </button>
            </div>
          </div>
        );
      case 'references':
        return (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={addReference}
                className="text-xs font-medium text-primary-600 hover:text-primary-800"
              >
                Add Reference
              </button>
            </div>
            <div className="space-y-4">
              {referencesForm.map((ref, idx) => (
                <div key={idx} className="border rounded-md p-3">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-gray-700">Reference {idx + 1}</span>
                    <button
                      type="button"
                      onClick={() => removeReference(idx)}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600">Name</label>
                      <input
                        type="text"
                        value={ref.name || ''}
                        onChange={(e) => updateReferenceField(idx, 'name', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600">Email</label>
                      <input
                        type="email"
                        value={ref.email || ''}
                        onChange={(e) => updateReferenceField(idx, 'email', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600">Phone</label>
                      <input
                        type="text"
                        value={ref.phone || ''}
                        onChange={(e) => updateReferenceField(idx, 'phone', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600">Relationship</label>
                      <select
                        value={ref.relationshipType || ''}
                        onChange={(e) => updateReferenceField(idx, 'relationshipType', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                      >
                        <option value="">Select...</option>
                        {REFERENCE_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600">Years Known</label>
                      <input
                        type="number"
                        value={ref.yearsKnown || ''}
                        onChange={(e) => updateReferenceField(idx, 'yearsKnown', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600">Organization</label>
                      <input
                        type="text"
                        value={ref.organization || ''}
                        onChange={(e) => updateReferenceField(idx, 'organization', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-600">Position</label>
                      <input
                        type="text"
                        value={ref.position || ''}
                        onChange={(e) => updateReferenceField(idx, 'position', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-600">Relationship Description</label>
                      <textarea
                        rows={2}
                        value={ref.relationshipDescription || ''}
                        onChange={(e) => updateReferenceField(idx, 'relationshipDescription', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleReferencesSave}
                disabled={updateReferencesMutation.isPending}
                className="inline-flex items-center px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {updateReferencesMutation.isPending ? 'Saving...' : 'Save References'}
              </button>
            </div>
          </div>
        );
      case 'theology':
        if (!theologyForm) return null;
        return (
          <div className="space-y-3">
            {THEOLOGY_FIELDS.map((field) => (
              <div key={field.key}>
                <label className="block text-xs font-medium text-gray-600">{field.label}</label>
                <textarea
                  rows={3}
                  value={theologyForm[field.key] || ''}
                  onChange={(e) => updateTheologyField(field.key, e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                />
              </div>
            ))}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleTheologySave}
                disabled={updateTheologyMutation.isPending}
                className="inline-flex items-center px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {updateTheologyMutation.isPending ? 'Saving...' : 'Save Theology'}
              </button>
            </div>
          </div>
        );
      case 'spiritual':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={spiritualFlags.candidateSubmitted}
                  onChange={(e) =>
                    setSpiritualFlags((prev) => ({
                      ...prev,
                      candidateSubmitted: e.target.checked,
                    }))
                  }
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">Candidate Submitted</span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={spiritualFlags.spouseSubmitted}
                  onChange={(e) =>
                    setSpiritualFlags((prev) => ({
                      ...prev,
                      spouseSubmitted: e.target.checked,
                    }))
                  }
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">Spouse Submitted</span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={spiritualFlags.spiritualHealthCompleted}
                  onChange={(e) =>
                    setSpiritualFlags((prev) => ({
                      ...prev,
                      spiritualHealthCompleted: e.target.checked,
                    }))
                  }
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">Spiritual Health Completed</span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600">Notes</label>
              <textarea
                rows={2}
                value={spiritualFlags.spiritualHealthNotes}
                onChange={(e) =>
                  setSpiritualFlags((prev) => ({
                    ...prev,
                    spiritualHealthNotes: e.target.value,
                  }))
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
              />
            </div>
            <div>
              <h6 className="text-sm font-semibold text-gray-800 mb-2">Candidate Survey</h6>
              <div className="space-y-3">
                {SPIRITUAL_HEALTH_FIELDS.map((field) => (
                  <div key={`candidate-${field.key}`}>
                    <label className="block text-xs font-medium text-gray-600">{field.label}</label>
                    <textarea
                      rows={3}
                      value={spiritualCandidateForm?.[field.key] || ''}
                      onChange={(e) => updateSpiritualCandidateField(field.key, e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h6 className="text-sm font-semibold text-gray-800 mb-2">Spouse Survey</h6>
              <div className="space-y-3">
                {SPIRITUAL_HEALTH_FIELDS.map((field) => (
                  <div key={`spouse-${field.key}`}>
                    <label className="block text-xs font-medium text-gray-600">{field.label}</label>
                    <textarea
                      rows={3}
                      value={spiritualSpouseForm?.[field.key] || ''}
                      onChange={(e) => updateSpiritualSpouseField(field.key, e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSpiritualSave}
                disabled={updateSpiritualMutation.isPending}
                className="inline-flex items-center px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {updateSpiritualMutation.isPending ? 'Saving...' : 'Save Spiritual Health'}
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const getEditSectionTitle = (section: string | null) => {
    switch (section) {
      case 'profile':
        return 'Edit Profile';
      case 'sendingChurch':
        return 'Edit Sending Church';
      case 'references':
        return 'Edit References';
      case 'theology':
        return 'Edit Theological Survey';
      case 'spiritual':
        return 'Edit Spiritual Health';
      default:
        return 'Edit Section';
    }
  };


  return (
    <Layout>
      <div className="px-4 sm:px-0">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Portal Candidate Onboarding</h1>
          <p className="mt-2 text-sm text-gray-600">
            Track and manage onboarding progress for plant portal candidates
          </p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-6 w-6 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                      />
                    </svg>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Candidates
                      </dt>
                      <dd className="text-2xl font-semibold text-gray-900">{stats.total}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-6 w-6 text-green-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Completed Onboarding
                      </dt>
                      <dd className="text-2xl font-semibold text-green-600">
                        {stats.completedOnboarding}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-6 w-6 text-blue-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">In Progress</dt>
                      <dd className="text-2xl font-semibold text-blue-600">
                        {stats.inProgress}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-6 w-6 text-yellow-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Passed Assessment
                      </dt>
                      <dd className="text-2xl font-semibold text-yellow-600">
                        {stats.byStatus?.assessment_passed || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
              <div>
                <input
                  type="text"
                  placeholder="Search candidates..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">All Status</option>
                  <option value="application_started">Application Started</option>
                  <option value="application_submitted">Application Submitted</option>
                  <option value="assessment_scheduled">Assessment Scheduled</option>
                  <option value="assessment_passed">Assessment Passed</option>
                </select>
              </div>
              <div>
                <select
                  value={onboardingFilter}
                  onChange={(e) => setOnboardingFilter(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">All Onboarding</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div>
                <button
                  onClick={() => {
                    setSearch('');
                    setStatusFilter('');
                    setOnboardingFilter('');
                  }}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Reset Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        {pdfError && (
          <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-3 flex items-start justify-between gap-4">
            <p className="text-sm text-red-700">{pdfError}</p>
            <button
              type="button"
              onClick={() => setPdfError(null)}
              className="text-sm font-medium text-red-700 hover:text-red-900"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Candidates Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="sm:hidden divide-y divide-gray-200">
            {isLoading ? (
              <div className="px-4 py-4 text-center text-sm text-gray-500">Loading...</div>
            ) : candidates.length === 0 ? (
              <div className="px-4 py-4 text-center text-sm text-gray-500">No candidates found</div>
            ) : (
              candidates.map((candidate: PortalCandidate) => (
                <div key={candidate.id} className="px-4 py-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{candidate.name}</p>
                      <p className="text-xs text-gray-500">ID: {candidate.id}</p>
                    </div>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                        candidate.portalStatus
                      )}`}
                    >
                      {candidate.portalStatus.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div className="text-sm text-gray-700">
                    <p>Email: {candidate.email || candidate.user?.email || 'N/A'}</p>
                    <p>
                      Submitted:{' '}
                      {candidate.applicationSubmittedAt
                        ? new Date(candidate.applicationSubmittedAt).toLocaleDateString()
                        : 'Not submitted'}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-700">
                          Portal • Step {candidate.onboardingStep} of 10
                        </span>
                        <span className="text-xs text-gray-500">{getPortalProgressPercent(candidate)}%</span>
                      </div>
                      <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
                        <div
                          style={{ width: `${getPortalProgressPercent(candidate)}%` }}
                          className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-700">Team</span>
                        <span className="text-xs text-gray-500">{getTeamProgressPercent(candidate)}%</span>
                      </div>
                      <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
                        <div
                          style={{ width: `${getTeamProgressPercent(candidate)}%` }}
                          className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-emerald-600"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 flex flex-wrap gap-2">
                    <span>
                      P&E:{' '}
                      {candidate.prepareEnrichRequested
                        ? candidate.prepareEnrichStatus?.replace(/_/g, ' ') || 'N/A'
                        : 'Not requested'}
                    </span>
                    <span>
                      BG:{' '}
                      {candidate.backgroundCheckSubmitted
                        ? candidate.backgroundCheckStatus?.replace(/_/g, ' ') || 'N/A'
                        : 'Not submitted'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        setSelectedCandidate(candidate);
                        setShowDetailModal(true);
                        setShowDeleteModal(false);
                        setDeleteError(null);
                      }}
                      className="text-sm font-medium text-primary-600 hover:text-primary-900"
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => handleDownloadPdf(candidate)}
                      disabled={pdfDownloadingId === candidate.id}
                      className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-60 disabled:cursor-not-allowed"
                      title="Download candidate details as PDF"
                    >
                      {pdfDownloadingId === candidate.id ? (
                        <>
                          <svg className="animate-spin -ml-0.5 mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          Generating
                        </>
                      ) : (
                        <>
                          <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M4 6h16M4 6a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V8a2 2 0 00-2-2" />
                          </svg>
                          PDF
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          <ResponsiveTableWrapper className="hidden sm:block">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    Candidate
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    Onboarding Progress
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    P&E Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    BG Check
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    Submitted
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-4 text-center text-sm text-gray-500 sm:px-6">
                      Loading...
                    </td>
                  </tr>
                ) : candidates.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-4 text-center text-sm text-gray-500 sm:px-6">
                      No candidates found
                    </td>
                  </tr>
                ) : (
                  candidates.map((candidate: PortalCandidate) => (
                    <tr key={candidate.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-normal sm:px-6 sm:whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                              <span className="text-primary-700 font-medium text-sm">
                                {candidate.name
                                  .split(' ')
                                  .map((n) => n[0])
                                  .join('')
                                  .substring(0, 2)}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{candidate.name}</div>
                            <div className="text-sm text-gray-500">ID: {candidate.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-normal text-sm text-gray-900 sm:px-6 sm:whitespace-nowrap">
                        {candidate.email || candidate.user?.email || 'N/A'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap sm:px-6">
                        <div className="flex items-center">
                          <div className="flex-1">
                            <div className="space-y-2">
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-medium text-gray-700">
                                    Portal • Step {candidate.onboardingStep} of 10
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {getPortalProgressPercent(candidate)}%
                                  </span>
                                </div>
                                <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
                                  <div
                                    style={{ width: `${getPortalProgressPercent(candidate)}%` }}
                                    className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500"
                                  />
                                </div>
                              </div>

                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-medium text-gray-700">Team</span>
                                  <span className="text-xs text-gray-500">
                                    {getTeamProgressPercent(candidate)}%
                                  </span>
                                </div>
                                <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
                                  <div
                                    style={{ width: `${getTeamProgressPercent(candidate)}%` }}
                                    className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-emerald-600"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap sm:px-6">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                            candidate.portalStatus
                          )}`}
                        >
                          {candidate.portalStatus.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap sm:px-6">
                        {candidate.prepareEnrichRequested ? (
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              candidate.prepareEnrichStatus === 'complete'
                                ? 'bg-green-100 text-green-800'
                                : candidate.prepareEnrichStatus === 'scheduled'
                                ? 'bg-blue-100 text-blue-800'
                                : candidate.prepareEnrichStatus === 'started'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {candidate.prepareEnrichStatus?.replace(/_/g, ' ') || 'N/A'}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Not requested</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap sm:px-6">
                        {candidate.backgroundCheckSubmitted ? (
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              candidate.backgroundCheckStatus === 'received'
                                ? 'bg-green-100 text-green-800'
                                : candidate.backgroundCheckStatus === 'submitted'
                                ? 'bg-blue-100 text-blue-800'
                                : candidate.backgroundCheckStatus === 'started'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {candidate.backgroundCheckStatus?.replace(/_/g, ' ') || 'N/A'}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Not submitted</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 sm:px-6">
                        {candidate.applicationSubmittedAt
                          ? new Date(candidate.applicationSubmittedAt).toLocaleDateString()
                          : 'Not submitted'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium sm:px-6">
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={() => {
                              setSelectedCandidate(candidate);
                              setShowDetailModal(true);
                              setShowDeleteModal(false);
                              setDeleteError(null);
                            }}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            View Details
                          </button>
                          <button
                            onClick={() => handleDownloadPdf(candidate)}
                            disabled={pdfDownloadingId === candidate.id}
                            className="inline-flex items-center text-blue-600 hover:text-blue-800 disabled:opacity-60 disabled:cursor-not-allowed"
                            title="Download candidate details as PDF"
                          >
                            {pdfDownloadingId === candidate.id ? (
                              <>
                                <svg className="animate-spin -ml-0.5 mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                Generating
                              </>
                            ) : (
                              <>
                                <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M4 6h16M4 6a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V8a2 2 0 00-2-2" />
                                </svg>
                                PDF
                              </>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </ResponsiveTableWrapper>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                  disabled={page === pagination.pages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing page <span className="font-medium">{page}</span> of{' '}
                    <span className="font-medium">{pagination.pages}</span>
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                      disabled={page === pagination.pages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Detail Modal */}
        {showDetailModal && selectedCandidate && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={closeDetailModal}
              ></div>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[85vh] overflow-y-auto">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl leading-6 font-bold text-gray-900">
                      Portal Candidate Details - {selectedCandidate.name}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${selectedCandidate.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {selectedCandidate.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  {/* Email Sharing Section */}
                  <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                      <h4 className="text-sm font-semibold text-gray-900">Share via Email</h4>
                      <button
                        type="button"
                        onClick={() => handleDownloadPdf(selectedCandidate)}
                        disabled={pdfDownloadingId === selectedCandidate.id}
                        className="inline-flex items-center px-3 py-2 border border-blue-600 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {pdfDownloadingId === selectedCandidate.id ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-700" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Generating PDF...
                          </>
                        ) : (
                          <>
                            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M4 6h16M4 6a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V8a2 2 0 00-2-2" />
                            </svg>
                            Download PDF
                          </>
                        )}
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        placeholder="Enter email address"
                        value={recipientEmail}
                        onChange={(e) => {
                          setRecipientEmail(e.target.value);
                          setEmailError(null);
                          setEmailSuccess(null);
                        }}
                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                      <button
                        onClick={handleSendEmail}
                        disabled={sendingEmail || !recipientEmail}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {sendingEmail ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Sending...
                          </>
                        ) : (
                          <>
                            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            Send Details
                          </>
                        )}
                      </button>
                    </div>
                    {emailSuccess && (
                      <p className="mt-2 text-sm text-green-600">{emailSuccess}</p>
                    )}
                    {emailError && (
                      <p className="mt-2 text-sm text-red-600">{emailError}</p>
                    )}
                    {pdfError && (
                      <p className="mt-2 text-sm text-red-600">{pdfError}</p>
                    )}
                  </div>

                  <div className="space-y-6">
                    {/* Basic Information */}
                    <div className="border-b pb-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-md font-semibold text-gray-900 flex items-center">
                          <svg className="h-5 w-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          Basic Information
                        </h4>
                        <button
                          type="button"
                          onClick={() => openEditModal('profile')}
                          className="text-xs font-medium text-primary-600 hover:text-primary-800"
                        >
                          Edit
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <DetailField label="ID" value={selectedCandidate.id} />
                        <DetailField label="Name" value={selectedCandidate.name} />
                        <DetailField label="First Name" value={(selectedCandidate as any).firstName} />
                        <DetailField label="Last Name" value={(selectedCandidate as any).lastName} />
                        <DetailField label="Email" value={selectedCandidate.email} />
                        <DetailField label="Phone" value={selectedCandidate.phone} />
                        <DetailField label="Date of Birth" value={(selectedCandidate as any).dateOfBirth} />
                        <DetailField label="Marital Status" value={(selectedCandidate as any).maritalStatus} />
                      </div>
                    </div>

                    {/* Spouse Information */}
                    {(selectedCandidate as any).maritalStatus === 'married' && (
                      <div className="border-b pb-4">
                        <h4 className="text-md font-semibold text-gray-900 mb-3">Spouse Information</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <DetailField label="Wife First Name" value={(selectedCandidate as any).wifeFirstName} />
                          <DetailField label="Wife Last Name" value={(selectedCandidate as any).wifeLastName} />
                          <DetailField label="Wife Email" value={(selectedCandidate as any).wifeEmail} />
                          <DetailField label="Wife Phone" value={(selectedCandidate as any).wifePhone} />
                        </div>
                      </div>
                    )}

                    {/* Family Information */}
                    <div className="border-b pb-4">
                      <h4 className="text-md font-semibold text-gray-900 mb-3">Family Information</h4>
                      <DetailField label="Children Names & Ages" value={(selectedCandidate as any).childrenNamesAges} fullWidth />
                    </div>

                    {/* Church Planting Information */}
                    <div className="border-b pb-4">
                      <h4 className="text-md font-semibold text-gray-900 mb-3">Church Planting Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <DetailField label="Target Location" value={(selectedCandidate as any).targetLocationMississippi} />
                        <DetailField label="Will Be Southern Baptist" value={(selectedCandidate as any).willBeSouthernBaptist} />
                        <DetailField label="Church Name" value={(selectedCandidate as any).churchName} />
                        <DetailField label="Supporting Church" value={(selectedCandidate as any).supportingChurch} />
                      </div>
                    </div>

                    {/* Ministry Experience */}
                    <div className="border-b pb-4">
                      <h4 className="text-md font-semibold text-gray-900 mb-3">Ministry Experience</h4>
                      <DetailField label="Leadership Positions" value={(selectedCandidate as any).ministryLeadershipPositions} fullWidth />
                      <DetailField label="Ministry Influences" value={(selectedCandidate as any).ministryInfluences} fullWidth />
                      <DetailField label="Theological Training" value={(selectedCandidate as any).theologicalTraining} fullWidth />
                      <DetailField label="Sermon Reference URL" value={(selectedCandidate as any).sermonReferenceUrl} fullWidth isLink />
                      <DetailField label="Will Email Sermon" value={formatBoolean((selectedCandidate as any).willEmailSermon)} />
                    </div>

                    {/* Onboarding Status */}
                    <div className="border-b pb-4">
                      <h4 className="text-md font-semibold text-gray-900 mb-3">Onboarding Status</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <DetailField label="Portal Status" value={selectedCandidate.portalStatus?.replace(/_/g, ' ')} />
                        <DetailField label="Onboarding Step" value={`${selectedCandidate.onboardingStep} of 8`} />
                        <DetailField label="Onboarding Completed" value={formatBoolean(selectedCandidate.onboardingCompleted)} />
                        <DetailField label="Profile Completed" value={formatBoolean((selectedCandidate as any).profileCompleted)} />
                        <DetailField label="Theology Completed" value={formatBoolean((selectedCandidate as any).theologyCompleted)} />
                        <DetailField label="References Completed" value={formatBoolean((selectedCandidate as any).referencesCompleted)} />
                        <DetailField label="Sending Church Completed" value={formatBoolean((selectedCandidate as any).sendingChurchCompleted)} />
                        <DetailField label="Application Submitted" value={formatDate(selectedCandidate.applicationSubmittedAt)} />
                      </div>
                    </div>

                    {/* Sending Church */}
                    <div className="border-b pb-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-md font-semibold text-gray-900">Sending Church</h4>
                        <button
                          type="button"
                          onClick={() => openEditModal('sendingChurch')}
                          className="text-xs font-medium text-primary-600 hover:text-primary-800"
                        >
                          Edit
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <DetailField label="No Sending Church" value={formatBoolean(modalCandidate?.sendingChurch?.noSendingChurch)} />
                        <DetailField label="Explanation" value={modalCandidate?.sendingChurch?.noSendingChurchExplanation} fullWidth />
                        <DetailField label="Church Name" value={modalCandidate?.sendingChurch?.churchName} />
                        <DetailField label="Denomination" value={modalCandidate?.sendingChurch?.denomination} />
                        <DetailField label="Primary Contact" value={modalCandidate?.sendingChurch?.primaryContactName} />
                        <DetailField label="Primary Contact Email" value={modalCandidate?.sendingChurch?.primaryContactEmail} />
                      </div>
                    </div>

                    {/* Prepare/Enrich Counseling */}
                    <div className="border-b pb-4">
                      <h4 className="text-md font-semibold text-gray-900 mb-3">Prepare/Enrich Counseling</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <DetailField label="Requested" value={formatBoolean(selectedCandidate.prepareEnrichRequested)} />
                        <DetailField label="Requested At" value={formatDate(selectedCandidate.prepareEnrichRequestedAt)} />
                        <DetailField label="Status" value={selectedCandidate.prepareEnrichStatus?.replace(/_/g, ' ')} />
                        <DetailField label="Status Updated" value={formatDate(selectedCandidate.prepareEnrichStatusUpdatedAt)} />
                      </div>
                      <DetailField label="Notes" value={selectedCandidate.prepareEnrichNotes} fullWidth />
                    </div>

                    {/* Background Check */}
                    <div className="border-b pb-4">
                      <h4 className="text-md font-semibold text-gray-900 mb-3">Background Check</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <DetailField label="Submitted" value={formatBoolean(selectedCandidate.backgroundCheckSubmitted)} />
                        <DetailField label="Submitted At" value={formatDate(selectedCandidate.backgroundCheckSubmittedAt)} />
                        <DetailField label="Status" value={selectedCandidate.backgroundCheckStatus?.replace(/_/g, ' ')} />
                        <DetailField label="Status Updated" value={formatDate(selectedCandidate.backgroundCheckStatusUpdatedAt)} />
                      </div>
                      <DetailField label="Notes" value={selectedCandidate.backgroundCheckNotes} fullWidth />
                    </div>

                    {/* Spiritual Health Survey Status */}
                    <div className="border-b pb-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-md font-semibold text-gray-900">Spiritual Health Survey Status</h4>
                        <button
                          type="button"
                          onClick={() => openEditModal('spiritual')}
                          className="text-xs font-medium text-primary-600 hover:text-primary-800"
                        >
                          Edit
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <DetailField label="Completed" value={formatBoolean((selectedCandidate as any).spiritualHealthCompleted)} />
                        <DetailField label="Candidate Submitted" value={formatBoolean((selectedCandidate as any).spiritualHealthCandidateSubmitted)} />
                        <DetailField label="Spouse Submitted" value={formatBoolean((selectedCandidate as any).spiritualHealthSpouseSubmitted)} />
                      </div>
                      <DetailField label="Notes" value={(selectedCandidate as any).spiritualHealthNotes} fullWidth />
                    </div>

                    {/* References */}
                    <div className="border-b pb-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-md font-semibold text-gray-900">References</h4>
                        <button
                          type="button"
                          onClick={() => openEditModal('references')}
                          className="text-xs font-medium text-primary-600 hover:text-primary-800"
                        >
                          Edit
                        </button>
                      </div>
                      {(modalCandidate?.references || []).length === 0 ? (
                        <p className="text-sm text-gray-500">No references on file yet.</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Relationship</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Contacted</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {(modalCandidate?.references || []).map((ref: any, idx: number) => (
                                <tr key={idx}>
                                  <td className="px-3 py-2 text-sm text-gray-900">{ref.name || 'N/A'}</td>
                                  <td className="px-3 py-2 text-sm text-gray-900">{ref.email || 'N/A'}</td>
                                  <td className="px-3 py-2 text-sm text-gray-900">{ref.phone || 'N/A'}</td>
                                  <td className="px-3 py-2 text-sm text-gray-900">{ref.relationship || 'N/A'}</td>
                                  <td className="px-3 py-2 text-sm text-gray-900">{formatBoolean(ref.contactedByHub)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* Theological Survey */}
                    <div className="border-b pb-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-md font-semibold text-gray-900">Theological Survey</h4>
                        <button
                          type="button"
                          onClick={() => openEditModal('theology')}
                          className="text-xs font-medium text-primary-600 hover:text-primary-800"
                        >
                          Edit
                        </button>
                      </div>
                      {!(selectedCandidate as any).theologicalSurvey ? (
                        <p className="text-sm text-gray-500">No theological survey on file yet.</p>
                      ) : (
                        <>
                        <div className="mb-4">
                          <h5 className="text-sm font-semibold text-gray-700 mb-2">Scripture</h5>
                          <DetailField label="Scripture Affirmation" value={(selectedCandidate as any).theologicalSurvey.scriptureAffirmation} fullWidth />
                        </div>

                        <div className="mb-4">
                          <h5 className="text-sm font-semibold text-gray-700 mb-2">God & Trinity</h5>
                          <DetailField label="Trinity Understanding" value={(selectedCandidate as any).theologicalSurvey.trinityUnderstanding} fullWidth />
                        </div>

                        <div className="mb-4">
                          <h5 className="text-sm font-semibold text-gray-700 mb-2">Humanity & Sin</h5>
                          <DetailField label="Original Sin Understanding" value={(selectedCandidate as any).theologicalSurvey.originalSinUnderstanding} fullWidth />
                        </div>

                        <div className="mb-4">
                          <h5 className="text-sm font-semibold text-gray-700 mb-2">Christology</h5>
                          <DetailField label="Christology Understanding" value={(selectedCandidate as any).theologicalSurvey.christologyUnderstanding} fullWidth />
                        </div>

                        <div className="mb-4">
                          <h5 className="text-sm font-semibold text-gray-700 mb-2">Salvation</h5>
                          <DetailField label="Salvation Doctrines" value={(selectedCandidate as any).theologicalSurvey.salvationDoctrines} fullWidth />
                          <DetailField label="Gospel Presentation" value={(selectedCandidate as any).theologicalSurvey.gospelPresentation} fullWidth />
                        </div>

                        <div className="mb-4">
                          <h5 className="text-sm font-semibold text-gray-700 mb-2">Ecclesiology</h5>
                          <DetailField label="Church Nature & Mission" value={(selectedCandidate as any).theologicalSurvey.churchNatureMission} fullWidth />
                          <DetailField label="Church Ordinances" value={(selectedCandidate as any).theologicalSurvey.churchOrdinances} fullWidth />
                          <DetailField label="Church Membership" value={(selectedCandidate as any).theologicalSurvey.churchMembership} fullWidth />
                          <DetailField label="Church Offices" value={(selectedCandidate as any).theologicalSurvey.churchOffices} fullWidth />
                        </div>

                        <div className="mb-4">
                          <h5 className="text-sm font-semibold text-gray-700 mb-2">Eschatology</h5>
                          <DetailField label="Eschatological Convictions" value={(selectedCandidate as any).theologicalSurvey.eschatologicalConvictions} fullWidth />
                          <DetailField label="Eschatology Ministry Impact" value={(selectedCandidate as any).theologicalSurvey.eschatologyMinistryImpact} fullWidth />
                        </div>

                        <div className="mb-4">
                          <h5 className="text-sm font-semibold text-gray-700 mb-2">Baptist Distinctives</h5>
                          <DetailField label="Believer's Baptism" value={(selectedCandidate as any).theologicalSurvey.believersBaptism} fullWidth />
                          <DetailField label="Church Autonomy" value={(selectedCandidate as any).theologicalSurvey.churchAutonomy} fullWidth />
                        </div>

                        <div className="mb-4">
                          <h5 className="text-sm font-semibold text-gray-700 mb-2">Ministry Philosophy & Practice</h5>
                          <DetailField label="Preaching Philosophy" value={(selectedCandidate as any).theologicalSurvey.preachingPhilosophy} fullWidth />
                          <DetailField label="Disciple Making" value={(selectedCandidate as any).theologicalSurvey.discipleMaking} fullWidth />
                          <DetailField label="Worship Philosophy" value={(selectedCandidate as any).theologicalSurvey.worshipPhilosophy} fullWidth />
                          <DetailField label="Evangelism & Social Engagement" value={(selectedCandidate as any).theologicalSurvey.evangelismSocialEngagement} fullWidth />
                          <DetailField label="Theological Disagreement" value={(selectedCandidate as any).theologicalSurvey.theologicalDisagreement} fullWidth />
                        </div>

                        <div className="mb-4">
                          <h5 className="text-sm font-semibold text-gray-700 mb-2">Missional & Cultural Engagement</h5>
                          <DetailField label="Gospel in Pluralistic Culture" value={(selectedCandidate as any).theologicalSurvey.gospelPluralisticCulture} fullWidth />
                          <DetailField label="Contextualization View" value={(selectedCandidate as any).theologicalSurvey.contextualizationView} fullWidth />
                          <DetailField label="Planting Challenges" value={(selectedCandidate as any).theologicalSurvey.plantingChallenges} fullWidth />
                        </div>

                        <div>
                          <h5 className="text-sm font-semibold text-gray-700 mb-2">Personal & Family Life</h5>
                          <DetailField label="Personal Holiness" value={(selectedCandidate as any).theologicalSurvey.personalHoliness} fullWidth />
                          <DetailField label="Marriage & Family Protection" value={(selectedCandidate as any).theologicalSurvey.marriageFamilyProtection} fullWidth />
                          <DetailField label="Conflict Handling" value={(selectedCandidate as any).theologicalSurvey.conflictHandling} fullWidth />
                        </div>
                        </>
                      )}
                    </div>

                    {/* Spiritual Health Surveys - Detailed */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-md font-semibold text-gray-900">Spiritual Health Surveys</h4>
                        <button
                          type="button"
                          onClick={() => openEditModal('spiritual')}
                          className="text-xs font-medium text-primary-600 hover:text-primary-800"
                        >
                          Edit
                        </button>
                      </div>
                      {(selectedCandidate as any).spiritualHealthSurveys && (selectedCandidate as any).spiritualHealthSurveys.length > 0 ? (
                        (selectedCandidate as any).spiritualHealthSurveys.map((survey: any, idx: number) => (
                          <div key={idx} className={idx > 0 ? 'mt-6 pt-6 border-t border-gray-200' : ''}>
                            <h5 className="text-md font-semibold text-blue-600 mb-3">
                              {survey.surveyFor === 'candidate' ? 'Candidate Survey' : 'Spouse Survey'}
                            </h5>

                            <div className="mb-4">
                              <h6 className="text-sm font-semibold text-gray-700 mb-2">Section 1: Spiritual Health Awareness</h6>
                              <DetailField label="Current Walk with the Lord" value={survey.currentWalkWithLord} fullWidth />
                              <DetailField label="Intimacy with Christ Hindrances" value={survey.intimacyWithChristHindrances} fullWidth />
                              <DetailField label="Spiritual Dryness Response" value={survey.spiritualDrynessResponse} fullWidth />
                              <DetailField label="Soul Guarding" value={survey.soulGuarding} fullWidth />
                              <DetailField label="Rest and Sabbath Rhythms" value={survey.restAndSabbathRhythms} fullWidth />
                              <DetailField label="Recent Conviction Response" value={survey.recentConvictionResponse} fullWidth />
                            </div>

                            <div className="mb-4">
                              <h6 className="text-sm font-semibold text-gray-700 mb-2">Section 2: Emotional Health Awareness</h6>
                              <DetailField label="Emotional Stability Description" value={survey.emotionalStabilityDescription} fullWidth />
                              <DetailField label="Pressure & Criticism Response" value={survey.pressureCriticismResponse} fullWidth />
                              <DetailField label="Stress & Disappointment Handling" value={survey.stressDisappointmentHandling} fullWidth />
                              <DetailField label="Healthy Boundaries" value={survey.healthyBoundaries} fullWidth />
                              <DetailField label="Current Emotional State" value={survey.currentEmotionalState} fullWidth />
                              <DetailField label="Burnout Prevention" value={survey.burnoutPrevention} fullWidth />
                            </div>

                            <div className="mb-4">
                              <h6 className="text-sm font-semibold text-gray-700 mb-2">Section 3: Calling and Resilience</h6>
                              <DetailField label="God's Call Description" value={survey.godsCallDescription} fullWidth />
                              <DetailField label="Call Confirmation by Others" value={survey.callConfirmationByOthers} fullWidth />
                              <DetailField label="Difficult Moments" value={survey.difficultMoments} fullWidth />
                              <DetailField label="Discerning God's Leading" value={survey.discerningGodsLeading} fullWidth />
                              <DetailField label="Faith and Wisdom Balance" value={survey.faithAndWisdomBalance} fullWidth />
                              <DetailField label="Church Planting Fears" value={survey.churchPlantingFears} fullWidth />
                            </div>

                            <div className="mb-4">
                              <h6 className="text-sm font-semibold text-gray-700 mb-2">Section 4: Relational Health Awareness</h6>
                              <DetailField label="Feedback & Correction Response" value={survey.feedbackCorrectionResponse} fullWidth />
                              <DetailField label="Conflict Resolution Approach" value={survey.conflictResolutionApproach} fullWidth />
                              <DetailField label="Shepherding Difficult People" value={survey.shepherdingDifficultPeople} fullWidth />
                              <DetailField label="Fostering Unity" value={survey.fosteringUnity} fullWidth />
                              <DetailField label="Relational Integrity" value={survey.relationalIntegrity} fullWidth />
                              <DetailField label="Accountability Relationships" value={survey.accountabilityRelationships} fullWidth />
                            </div>

                            <div className="mb-4">
                              <h6 className="text-sm font-semibold text-gray-700 mb-2">Section 5: Spiritual Warfare Health Awareness</h6>
                              <DetailField label="Spiritual Attack Recognition" value={survey.spiritualAttackRecognition} fullWidth />
                              <DetailField label="Prayer in Decisions" value={survey.prayerInDecisions} fullWidth />
                              <DetailField label="Guarding Against Cynicism" value={survey.guardingAgainstCynicism} fullWidth />
                              <DetailField label="Godly Provision Example" value={survey.godlyProvisionExample} fullWidth />
                              <DetailField label="Praying Together" value={survey.prayingTogether} fullWidth />
                              <DetailField label="Sustaining Scriptures" value={survey.sustainingScriptures} fullWidth />
                            </div>

                            <div className="mb-4">
                              <h6 className="text-sm font-semibold text-gray-700 mb-2">Section 6: Accountability Health Awareness</h6>
                              <DetailField label="Pastor/Mentor Accountability" value={survey.pastorMentorAccountability} fullWidth />
                              <DetailField label="Discipleship Frequency" value={survey.discipleshipFrequency} fullWidth />
                              <DetailField label="Teachability" value={survey.teachability} fullWidth />
                              <DetailField label="Isolation Prevention" value={survey.isolationPrevention} fullWidth />
                              <DetailField label="Spouse Support Needs" value={survey.spouseSupportNeeds} fullWidth />
                              <DetailField label="Handling Unappreciation" value={survey.handlingUnappreciation} fullWidth />
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">No spiritual health surveys on file yet.</p>
                      )}
                    </div>

                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={closeDetailModal}
                    disabled={deleteCandidateMutation.isPending}
                    className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    Close
                  </button>
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => {
                        setDeleteError(null);
                        setShowDeleteModal(true);
                      }}
                      disabled={deleteCandidateMutation.isPending}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                    >
                      Delete Candidate
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Section Edit Modal */}
        {showEditModal && activeEditSection && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={closeEditModal}
              ></div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[85vh] overflow-y-auto">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg leading-6 font-semibold text-gray-900">
                      {getEditSectionTitle(activeEditSection)}
                    </h3>
                    {isDetailLoading && (
                      <span className="text-xs text-gray-500">Loading latest details...</span>
                    )}
                  </div>
                  {saveError && <p className="mb-3 text-sm text-red-600">{saveError}</p>}
                  {saveSuccess && <p className="mb-3 text-sm text-green-600">{saveSuccess}</p>}
                  {renderEditModalContent()}
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={closeEditModal}
                    className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedCandidate && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() => {
                  if (!deleteCandidateMutation.isPending) {
                    setShowDeleteModal(false);
                    setDeleteError(null);
                  }
                }}
              ></div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">
                        Delete Candidate
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500 mb-2">
                          Are you sure you want to delete <span className="font-semibold">{selectedCandidate.name}</span>?
                        </p>
                        <p className="text-sm text-gray-500">
                          This action cannot be undone. The candidate record and associated user account will be permanently removed.
                        </p>
                        {deleteError && (
                          <p className="mt-3 text-sm text-red-600">{deleteError}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    disabled={deleteCandidateMutation.isPending}
                    onClick={() => deleteCandidateMutation.mutate(selectedCandidate.id)}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {deleteCandidateMutation.isPending ? 'Deleting...' : 'Delete Candidate'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteModal(false);
                      setDeleteError(null);
                    }}
                    disabled={deleteCandidateMutation.isPending}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

// Helper component for displaying field details
const DetailField: React.FC<{
  label: string;
  value: any;
  fullWidth?: boolean;
  isLink?: boolean;
}> = ({ label, value, fullWidth = false, isLink = false }) => {
  const displayValue = value ?? 'N/A';
  
  return (
    <div className={fullWidth ? 'col-span-full' : ''}>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      {isLink && value ? (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 text-sm text-blue-600 hover:text-blue-800 underline break-all"
        >
          {displayValue}
        </a>
      ) : (
        <p className="mt-1 text-sm text-gray-900 break-words whitespace-pre-wrap">
          {displayValue}
        </p>
      )}
    </div>
  );
};

export default PortalCandidates;


